/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-cycle */
import styled from '@emotion/styled';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { useEffect, useMemo, useState } from 'react';
import Image from '@base/FutureImage';
import { subscriptionTypes } from '@constants/index';
import usePricings from '@cont/Checkout/usePricings';
import { useRouter } from 'next/router';
import LeftChevron from 'aquilla/icons/actions/LeftChevron';
import usePurchaseDetails from '@cont/Checkout/usePurchaseDetails';
import useFetch from '@hooks/useFetch';
import Typography from 'aquilla/core/atoms/Typography';
import NormalPlanSelectionFlowPage from './NormalPlanSelectionPage';
import AddIconicFlowPage from './AddIconicFlowPage';
import AddonsFlowPage from './AddonsFlowPage';
import {
  HeaderWrapper,
  TopWrapper,
  StyLeftChevron,
  HelpWrapper
} from '../PlanSelection';

export const NORMALFLOWSTEPS = {
  PLANSELECTION: 1,
  ADDICONIC: 2,
  ADDON: 3
};

const Loader = styled(CircularLoader)`
  margin: auto;
`;

const LeftChevronWrapper = styled.div``;

const NormalPlanSelectionWrapper = styled.div`
  position: relative;
`;

const Title = styled(Typography)`
  width: 300px;
`;

const NormalPlanSelection = ({ setIsHelpDrawerOpen }) => {
  const { query: { goalUID = '', is_combo = false } = {} } = useRouter();

  const [flowSteps, setFlowSteps] = useState(
    is_combo ? NORMALFLOWSTEPS?.ADDON : NORMALFLOWSTEPS?.PLANSELECTION
  );

  const [selectedPlan, setSelectedPlan] = useState<
    Record<string, any> | undefined
  >();
  const [referralCode, setReferralCode] = useState<string>();
  const [bundleAddOnData, setBundleAddOnData] = useState<
    Record<string, any> | undefined
  >();
  const [preFilteredSubscriptionLists, setPreFilteredSubscriptionLists] =
    useState<Record<string, any> | undefined>();
  const [previousStep, setPreviousStep] = useState<number>();
  const [currentEventProps, setCurrentEventProps] = useState<
    Record<string, any> | undefined
  >();

  const { pricings } = usePricings({ goalUID });
  const { goal = {} } = pricings ?? {};

  useEffect(() => {
    const preFilteredSubscriptionLists = pricings?.subscriptions
      ? [...pricings.subscriptions].reduce(
          (acc, pricing) => {
            if (pricing.type === subscriptionTypes.PLUS) {
              acc.plus.push(pricing);
            } else if (pricing.type === subscriptionTypes.ICONIC) {
              acc.iconic[pricing.duration] = pricing;
            }
            return acc;
          },
          { plus: [], iconic: {} }
        )
      : [];

    setPreFilteredSubscriptionLists(preFilteredSubscriptionLists);
  }, [pricings?.subscriptions, referralCode]);

  const {
    purchaseDetails,
    purchaseDetailsWithoutDiscount,
    referral_code,
    validateReferralInProgress,
    referralError,
    isError
  } = usePurchaseDetails({ subscriptionUid: selectedPlan?.uid, referralCode });

  const {
    data: discountedPricings,
    isError: referralAllError,
    isLoading: referralAllLoading
  } = useFetch(
    referral_code && goal?.uid
      ? `v2/subscriptions/validate_referral_all_subscriptions/?referral=${encodeURIComponent(
          referralCode
        )}&goal=${goal?.uid}&iconic=${
          goal?.is_iconic_available
        }&upgrade=true&scheme=2&iconic_pro=${goal?.is_iconic_pro_available}`
      : null,
    { useCamelCase: false },
    { shouldRetryOnError: false }
  );

  const referralValid = !referralAllLoading && !referralAllError;

  const discountedPricingLists = useMemo(() => {
    return discountedPricings
      ? discountedPricings.reduce(
          (acc, pricing) => {
            const newPricing = {
              ...pricing?.subscription,
              referral_details: { ...pricing?.referral }
            };
            if (pricing?.subscription?.type === subscriptionTypes.PLUS) {
              acc.plus.push(newPricing);
            } else if (
              pricing?.subscription?.type === subscriptionTypes.ICONIC
            ) {
              acc.iconic[pricing?.subscription?.duration] = newPricing;
            }
            return acc;
          },
          { plus: [], iconic: {} }
        )
      : [];
  }, [discountedPricings]);

  useEffect(() => {
    if (referralCode && referralValid)
      setPreFilteredSubscriptionLists(discountedPricingLists);
  }, [
    discountedPricingLists,
    referralAllError,
    referralAllLoading,
    referralCode,
    referralValid
  ]);

  const { duration: plusDuration = '' } = selectedPlan ?? {};
  const preFilteredIconicSubscriptionLists = useMemo(() => {
    return preFilteredSubscriptionLists?.iconic ?? {};
  }, [preFilteredSubscriptionLists?.iconic]);

  const hasIconicSubscription =
    preFilteredIconicSubscriptionLists[plusDuration];

  const preFilteredPlusSubscriptionLists = useMemo(() => {
    const allowedPreFilteredSubscriptionLists =
      preFilteredSubscriptionLists?.plus?.filter((plan) => {
        return !!plan?.is_purchase_allowed;
      });
    return allowedPreFilteredSubscriptionLists?.reverse() ?? [];
  }, [preFilteredSubscriptionLists?.plus]);

  const showBackButton = previousStep && flowSteps !== previousStep;

  const RenderNormalComponent = () => {
    if (flowSteps) {
      switch (flowSteps) {
        case NORMALFLOWSTEPS.PLANSELECTION:
          return preFilteredSubscriptionLists ? (
            <NormalPlanSelectionFlowPage
              setFlowSteps={setFlowSteps}
              setSelectedPlan={setSelectedPlan}
              selectedPlan={selectedPlan}
              preFilteredPlusSubscriptionLists={
                preFilteredPlusSubscriptionLists
              }
              referralCode={referralCode}
              purchaseDetails={purchaseDetails}
              setReferralCode={setReferralCode}
              referralError={referralError}
              validateReferralInProgress={validateReferralInProgress}
              hasIconicSubscription={hasIconicSubscription}
              setBundleAddOnData={setBundleAddOnData}
              goalDetails={goal}
              setPreviousStep={setPreviousStep}
              referralValid={referralValid}
              referralAllLoading={referralAllLoading}
              discountedPricings={discountedPricings}
              setCurrentEventProps={setCurrentEventProps}
              showBackButton={showBackButton}
            />
          ) : (
            <></>
          );
        case NORMALFLOWSTEPS.ADDICONIC:
          return purchaseDetails ? (
            <AddIconicFlowPage
              setFlowSteps={setFlowSteps}
              hasIconicSubscription={hasIconicSubscription}
              preFilteredSubscriptionLists={preFilteredSubscriptionLists}
              purchaseDetails={purchaseDetails}
              setBundleAddOnData={setBundleAddOnData}
              setPreviousStep={setPreviousStep}
              discountedPricings={discountedPricings}
              setCurrentEventProps={setCurrentEventProps}
              currentEventProps={currentEventProps}
            />
          ) : (
            <></>
          );
        case NORMALFLOWSTEPS.ADDON:
          return purchaseDetails || is_combo ? (
            <AddonsFlowPage
              bundleAddOnData={bundleAddOnData}
              purchaseDetails={purchaseDetails}
              discountedPricings={discountedPricings}
              currentEventProps={currentEventProps}
            />
          ) : (
            <></>
          );
        default:
          return (
            <NormalPlanSelectionFlowPage
              setFlowSteps={setFlowSteps}
              setSelectedPlan={setSelectedPlan}
              selectedPlan={selectedPlan}
              preFilteredPlusSubscriptionLists={
                preFilteredPlusSubscriptionLists
              }
              referralCode={referralCode}
              purchaseDetails={purchaseDetails}
              setReferralCode={setReferralCode}
              referralError={referralError}
              validateReferralInProgress={validateReferralInProgress}
              hasIconicSubscription={hasIconicSubscription}
              setBundleAddOnData={setBundleAddOnData}
              goalDetails={goal}
              setPreviousStep={setPreviousStep}
              referralValid={referralValid}
              referralAllLoading={referralAllLoading}
              discountedPricings={discountedPricings}
              setCurrentEventProps={setCurrentEventProps}
              showBackButton={showBackButton}
            />
          );
      }
    } else {
      return <Loader color="var(--color-i-black)" />;
    }
  };

  return (
    <>
      <HeaderWrapper>
        <TopWrapper $showBackButton={showBackButton}>
          {showBackButton ? (
            <LeftChevronWrapper
              onClick={() => {
                setFlowSteps(previousStep);
              }}
            >
              <LeftChevron height={24} width={24} />
            </LeftChevronWrapper>
          ) : !is_combo ? (
            <Title variant="h2">
              Select duration for your {goal?.name} prep
            </Title>
          ) : (
            <div />
          )}
          <HelpWrapper
            onClick={() => {
              setIsHelpDrawerOpen(true);
            }}
            $showBackButton={showBackButton}
          >
            <Image
              src="newPlanSelection/callfilledIcon.svg"
              width={16}
              height={16}
              alt="call icon"
            />
            Help
          </HelpWrapper>
        </TopWrapper>
      </HeaderWrapper>
      <NormalPlanSelectionWrapper>
        {RenderNormalComponent()}
      </NormalPlanSelectionWrapper>
    </>
  );
};

export default NormalPlanSelection;
